import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ESCALATION_LABELS, SLA_COLORS, STAGE_COLORS, STAGES, fmtMoney } from "@/lib/constants";

export default function Queue() {
  const [clients, setClients] = useState(null);
  const [filter, setFilter] = useState("All");
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!clients) return [];
    return filter === "All" ? clients : clients.filter((c) => c.stage === filter);
  }, [clients, filter]);

  if (!clients) {
    return <div className="font-mono text-zinc-500 text-sm animate-pulse" data-testid="queue-loading">LOADING QUEUE…</div>;
  }

  return (
    <div className="space-y-5" data-testid="queue-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-100">Prioritised Client Queue</h1>
        <p className="text-sm text-zinc-500 mt-1">Ranked by AI risk / opportunity score. Click any client to open the full intelligence profile.</p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="stage-filters">
        {["All", ...STAGES].map((s) => (
          <button
            key={s}
            data-testid={`filter-${s.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-sm text-xs font-semibold border transition-colors duration-200 ${
              filter === s
                ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/50"
                : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-zinc-600 hover:text-zinc-200"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[1200px]" data-testid="client-queue-table">
          <thead>
            <tr className="border-b border-zinc-800 text-left">
              <th className="px-5 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider text-right">Score</th>
              <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Client</th>
              <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Country / Lang</th>
              <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Jurisdiction</th>
              <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Stage</th>
              <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Stuck At</th>
              <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Reason Detected</th>
              <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider text-right">Est. Value</th>
              <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Channel</th>
              <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Assignee</th>
              <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">SLA</th>
              <th className="px-5 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Escalation</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c, i) => (
              <tr
                key={c.id}
                data-testid={`queue-row-${c.id}`}
                style={{ animationDelay: `${i * 30}ms` }}
                onClick={() => navigate(`/clients/${c.id}`)}
                className="fade-slide-in border-b border-zinc-800/60 hover:bg-zinc-800/50 cursor-pointer transition-colors duration-150"
              >
                <td className="px-5 py-3 text-right">
                  <span className={`font-mono font-semibold ${c.score >= 80 ? "text-red-400" : c.score >= 60 ? "text-amber-400" : "text-emerald-400"}`}>
                    {c.score}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="font-semibold text-zinc-100">{c.name}</div>
                  <div className="text-[11px] font-mono text-zinc-500">{c.id}</div>
                </td>
                <td className="px-4 py-3 text-xs text-zinc-400">{c.country}<div className="text-zinc-600">{c.language}</div></td>
                <td className="px-4 py-3 text-xs text-zinc-400">{c.jurisdiction}</td>
                <td className="px-4 py-3">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${STAGE_COLORS[c.stage] || ""}`}>{c.stage}</span>
                </td>
                <td className="px-4 py-3 text-xs text-zinc-400 max-w-[220px] truncate" title={c.stuck_at}>{c.stuck_at}</td>
                <td className="px-4 py-3 text-xs text-zinc-400 max-w-[220px] truncate" title={c.reason}>{c.reason}</td>
                <td className="px-4 py-3 text-right font-mono text-zinc-200 whitespace-nowrap">{fmtMoney(c.value)}</td>
                <td className="px-4 py-3 text-xs text-zinc-300">{c.channel}</td>
                <td className="px-4 py-3 text-xs text-zinc-300 whitespace-nowrap">{c.assignee}</td>
                <td className={`px-4 py-3 text-xs whitespace-nowrap ${SLA_COLORS[c.sla.status] || "text-zinc-400"}`}>{c.sla.label}</td>
                <td className="px-5 py-3 text-xs">
                  <span className={c.escalation === "none" ? "text-zinc-600" : "text-red-400 font-semibold"}>
                    {ESCALATION_LABELS[c.escalation]}
                  </span>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={12} className="px-6 py-8 text-center text-sm text-zinc-500">No clients in this stage.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
